import { amountToTotalTax } from './amount-to-total-tax.function';

/**
 * Calculates a pricing item’s total tax.
 * @param pricingItem  The item to price:
 * 	- `units`         – how many units
 * 	- `unitPrice`     – price per unit (net or gross)
 * 	- `unitTaxRate`   – tax rate as a percentage (e.g. 19 for 19%)
 * 	- `isTaxIncluded` – whether `unitPrice` already includes tax (gross). Defaults to false.
 * @returns The total tax for all units.
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: false,
 * };
 * const totalTax = unitPricingToTotalTax(pricingItem);
 * console.log(totalTax); // Output: 76
 * ```
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: true,
 * };
 * const totalTax = unitPricingToTotalTax(pricingItem);
 * console.log(totalTax); // Output: 63.86554621848739
 * ```
 */
export function unitPricingToTotalTax(pricingItem: {
	units?: number;
	unitPrice?: number;
	unitTaxRate?: number;
	isTaxIncluded?: boolean;
}): number {
	if (!pricingItem) {
		return 0;
	}

	// Sanitize
	const units =
		pricingItem.units == null ||
		isNaN(pricingItem.units) ||
		pricingItem.units < 0
			? 0
			: pricingItem.units;
	const unitPrice =
		pricingItem.unitPrice == null || isNaN(pricingItem.unitPrice)
			? 0
			: pricingItem.unitPrice;
	const unitTaxRate =
		isNaN(pricingItem.unitTaxRate ?? NaN) || (pricingItem.unitTaxRate ?? 0) < 0
			? 0
			: pricingItem.unitTaxRate;

	// Calculate total tax for all units
	return (
		units * amountToTotalTax(unitPrice, unitTaxRate, pricingItem.isTaxIncluded)
	);
}
